/**
 * ⚠️ THIS PAGE'S ONE SEMANTIC IMPORT. /listen is deliberately hand-rolled — its own CSS, its own
 * tokens, no component library — because it is the only page a stranger sees. The exception is the
 * help popover: Semantic is already in the bundle, its CSS is global, and Popup is the app's own
 * idiom for "show this beside that". Writing a portal and its positioning by hand to avoid one
 * import would be the worse trade.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Popup, List } from 'semantic-ui-react';
import detectPlatform, { PLATFORM_LABELS } from '../../utils/detectPlatform';
import { config } from '../../constants/config';
import logo from '../../assets/authorator_logo_no-dot.png';
// import girlAudioBookListener from '../../assets/shutterstock_2294369429.png'
/**
 * ⭐ WEBP AT 700px, NOT THE 1024px PNGs (2026-10-11). The four sources were 1024x1024 PNGs
 * totalling 8.7 MB in the build, displayed in a 350px slot — and because the hero cycles, the page
 * fetches ALL FOUR rather than one. On /listen, which a client opens on whatever device they have
 * in order to download an audiobook, that was by far the heaviest thing on the page.
 *
 * Re-encoded with the project's own ffmpeg-static (lanczos, quality 82): 9.13 MB -> 651 KB, a 93%
 * cut, at 2x the 350px slot so it stays sharp on a retina screen. The .png originals are kept in
 * assets/ and are no longer imported — webpack only emits what is imported, so they cost nothing in
 * the bundle while the source material stays in the repo.
 *
 * ⚠️ No <picture> fallback. WebP is the only format offered, which is fine for a DECORATIVE hero —
 * a browser too old to decode it shows no picture and the page still works. If this image ever
 * carries meaning, it needs a fallback source and this comment is wrong.
 */
import listenImage1 from '../../assets/listen-image-1.webp';
import listenImage2 from '../../assets/listen-image-2.webp';
import listenImage3 from '../../assets/listen-image-3.webp';
import listenImage4 from '../../assets/listen-image-4.webp';

import './ListenPage.css';
import '../../styles/landingPage.css';

import '../../index.css'
/**
 * ListenPage — /listen, the client download page.
 *
 * For someone WITHOUT an app account: enter the access key you were sent, see the files released to
 * it, tap one to download it. Files are played from the phone's Files app; nothing streams here.
 *
 * Deliberately outside <Layout /> and outside the app's own API client:
 *  - no navbar, no app login, no app styling to fight on a phone;
 *  - `fetch` directly, so an app login token in this browser is never sent to /api/listen/*
 *    (listenAuth would refuse it anyway — the two credentials do not cross).
 *
 * The key is typed into a form and exchanged for a listener token; the KEY itself is never stored
 * or put in a URL. The token lives in sessionStorage and is gone when the tab closes.
 *
 * ⚠️ iOS: Safari ignores `download` on cross-origin links, so the server's signed URL forces the
 * download itself (Content-Disposition: attachment). Here we only navigate to it.
 *
 * See docs/journal/CLIENT_LISTEN_PAGE_DESIGN.md.
 */

/**
 * ⛔ ITS OWN STORAGE KEY, NEVER THE APP'S `authToken`.
 *
 * A reader now signs in through /api/login like anyone else, so the token here is an ordinary app
 * token. Storing it under the app's usual key would mean a reader signing in on this browser
 * CLOBBERS the operator's Studio session — and the operator testing /listen would log themselves
 * out of the app. Two independent sessions instead, and this page never touches `authToken`.
 *
 * ⚠️ localStorage, NOT sessionStorage, and the account is what changes that calculation. A 130-bit
 * bearer key left on a shared device was a credential nobody chose to leave; a session for an
 * account the reader created is ordinary web behaviour, and "Sign out" is the control. Without it,
 * a reader re-enters a 26-character code every single visit — which is unusable once they hold
 * several books.
 */
const AUTH_KEY = 'aai.listen.auth';
const NAME_KEY = 'aai.listen.name';
const API = config.BASE_URL;
const LARGE_FILE = 100 * 1048576;

const store = {
  get: () => { try { return localStorage.getItem(AUTH_KEY); } catch (_) { return null; } },
  set: (t) => { try { localStorage.setItem(AUTH_KEY, t); } catch (_) { /* private mode: this visit only */ } },
  getName: () => { try { return localStorage.getItem(NAME_KEY); } catch (_) { return null; } },
  setName: (n) => { try { if (n) localStorage.setItem(NAME_KEY, n); } catch (_) { /* header name only */ } },
  clear: () => {
    try { localStorage.removeItem(AUTH_KEY); localStorage.removeItem(NAME_KEY); } catch (_) { /* nothing to clear */ }
  },
};

const fmtDuration = (ms) => {
  if (!ms) return null;
  const s = Math.round(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h ? `${h} hr ${m} min` : `${Math.max(m, 1)} min`;
};
const fmtSize = (b) => {
  if (!b) return null;
  const mb = b / 1048576;
  return mb >= 1000 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
};

async function call(path, { method = 'GET', body, token } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { /* empty body */ }
  return { status: res.status, ok: res.ok, data: data || {} };
}

/**
 * The header image, crossfading between the four listener pictures.
 *
 * ⭐ ALL FOUR ARE RENDERED AND STACKED, rather than one <img> whose `src` is swapped. Swapping the
 * src cannot crossfade: the old frame is gone the instant the new one is assigned, and the new one
 * is not decoded yet, so the fade plays over a blank box. Stacked, the outgoing picture is still
 * there to fade out underneath the incoming one.
 *
 * ⚠️ THE FIRST IMAGE IS THE SIZER. It stays in normal flow and the other three are absolute on top
 * of it, so the container takes its height from a real image and nothing jumps when the page loads
 * or the window resizes. The four are the same 1024x1024, which is what makes that safe — a
 * differently-shaped fifth picture would need the wrapper to carry an explicit aspect-ratio.
 *
 * ⚠️ ONLY THE FIRST CARRIES ALT TEXT. They are four pictures of one thing, so to a screen reader
 * the other three are decoration; four identical alts would be read as four images.
 */
const HERO_IMAGES = [listenImage1, listenImage2, listenImage3, listenImage4];
const HERO_INTERVAL_MS = 30000;

function HeroImages() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    /**
     * ⛔ NOT CYCLED WHEN THE VIEWER ASKS FOR LESS MOTION. An unprompted animation that repeats
     * forever is the case `prefers-reduced-motion` exists for, and this one carries no information
     * — the page reads identically with one picture. So the preference stops the cycle outright
     * rather than merely shortening the fade.
     */
    const reduced = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;

    const id = setInterval(
      () => setCurrent((i) => (i + 1) % HERO_IMAGES.length),
      HERO_INTERVAL_MS,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div className="listen-hero">
      {HERO_IMAGES.map((src, i) => (
        <img
          key={src}
          className={`landingPgBackgroundImg listen-hero-img${i === current ? ' is-current' : ''}`}
          src={src}
          alt={i === 0 ? 'Audiobook Reader' : ''}
          aria-hidden={i === 0 ? undefined : 'true'}
          title="Happy Listener"
        />
      ))}
    </div>
  );
}

/**
 * The key box — the first thing a reader sees, because it is the only thing they have.
 *
 * ⭐ KEY FIRST, NOT ACCOUNT FIRST. A reader arrives holding a code, not knowing an account exists,
 * so the code is the single call to action in whatever message was sent to them. `key-status` then
 * says which door to open.
 *
 * ⚠️ `needs-account` IS A DEFAULT, NOT A VERDICT. A brand-new key sent to somebody who already
 * registered for an earlier book is indistinguishable from a first-timer's, because the key has
 * never met them — and with re-issues being the norm that is the common case, not an edge. So the
 * sign-in door is always offered too, never hidden behind the server's guess.
 *
 * ⛔ AND `onWantSignIn` IS WHY A RETURNING READER IS NOT STRANDED. Until it existed, AccountForm
 * rendered only when a key was in hand, so the only route to the sign-in form ran through a working
 * key — which made three things false at once: "Sign out" promised *"Your books stay on your
 * account"* while locking out anyone who had since discarded the code; revoking a key after
 * redemption closed the door on an entitlement that was still live; and the designed password
 * recovery ("ask for a new key") depended on the very gate it was recovering from. An account that
 * cannot be signed in to is not an account.
 *
 * ⓘ The prop is OPTIONAL and the link appears only when it is passed, because this same form is
 * reused inside the library for "Add another access key" — where the reader is already signed in
 * and a sign-in link would be nonsense.
 */
function KeyForm({ onKey, notice, onWantSignIn }) {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await call('/listen/key-status', { method: 'POST', body: { key } });
      if (r.status === 429) { setError(r.data.error || 'Too many attempts. Please wait a few minutes.'); return; }
      if (!r.ok || r.data.status === 'invalid') {
        setError('That key is not valid. Check it, or ask for a new one.');
        return;
      }
      // The key is held in memory only, and redeemed once there is an account to attach it to.
      onKey(key.trim(), r.data.status, r.data.label || null);
    } catch (_) {
      setError('Could not reach the server. Check your connection and try again.');
    } finally { setBusy(false); }
  };

  return (
    <form className="lp-card" onSubmit={submit} autoComplete="off">
      {/* An h1, not a span: this is the page's heading and a screen reader needs the landmark.
          `.listen-page .lp-prompt` is scoped so it outranks `.listen-page h1` — see the CSS. */}
      <h1 className="lp-prompt">Your audiobook</h1>
      <p className="lp-muted">Enter the access key you were sent.</p>
      {notice && <p className="lp-notice">{notice}</p>}
      <label htmlFor="lp-key" className="lp-visually-hidden">Access key</label>
      <input
        id="lp-key"
        className="lp-input"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="XXXX-XXXX-XXXX-…"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        inputMode="text"
        enterKeyHint="go"
      />
      {error && <p className="lp-error" role="alert">{error}</p>}
      <button className="lp-button lp-primary" type="submit" disabled={busy || !key.trim()}>
        {busy ? 'Checking…' : 'Continue'}
      </button>
      {onWantSignIn && (
        <p className="lp-help lp-small">
          Been here before?{' '}
          <button type="button" className="lp-link" onClick={onWantSignIn}>
            Sign in
          </button>
        </p>
      )}
    </form>
  );
}

/**
 * Create an account, or sign in to one — the same card, two modes.
 *
 * ⛔ NOTHING IS EMAILED, so there is no verification step and no "forgot password" link: there is no
 * mailer in this project. A reader who forgets asks for a new key, which re-redeems onto the same
 * account; that is the designed recovery and it is why `redeem` is idempotent.
 */
/**
 * ⭐ TWO WAYS IN, ONE FORM. A reader arrives either holding a key they can type or from a mailed
 * link. `pendingInvite` is the second: `{ token, email, titles, groups, label }` as
 * `invite-status` answered it.
 *
 * ⚠️ AN INVITATION MAY BE BOUND TO AN ADDRESS, and when it is, the email is PREFILLED AND
 * LOCKED. The server refuses a mismatch (EMAIL_MISMATCH), so letting somebody type a different
 * address would be offering a form that cannot succeed — the refusal has to happen before the
 * typing, not after it.
 */
function AccountForm({ mode, setMode, pendingKey, keyLabel, pendingInvite, onAuthed, onBack }) {
  const [email, setEmail] = useState((pendingInvite && pendingInvite.email) || '');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const creating = mode === 'create';
  // ⓘ An invitation can arrive after this form has mounted (a reader who chose "Sign in" first),
  // so the bound address is synced rather than only seeded.
  const boundEmail = (pendingInvite && pendingInvite.email) || null;
  useEffect(() => {
    if (boundEmail) setEmail(boundEmail);
  }, [boundEmail]);

  // ⛔ Either credential earns an account. Neither one, no account — see the notice below.
  const invited = !!(pendingKey || pendingInvite);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(null);
    try {
      /**
       * ⛔ THE KEY GOES WITH THE SIGNUP. Registration is by invitation now: the server requires a
       * valid unredeemed key before it will create anything, which is both what closes an endpoint
       * that was open to the world and what earns it a bypass of the production registration
       * kill-switch. No key, no account.
       *
       * ⓘ And the server REDEEMS it in the same call, so `keyConsumed` tells the page not to redeem
       * it again — a second attempt would succeed (redemption is idempotent) but announce "already
       * yours, access extended" to somebody who just signed up, which reads as a mistake.
       */
      let keyConsumed = false;
      if (creating) {
        /**
         * ⓘ ONE OR THE OTHER, never both. The server accepts `key` or `inviteToken`; sending
         * both would make which one was spent a matter of server-side precedence rather than of
         * what the reader actually arrived with.
         */
        const reg = await call('/listen/register', {
          method: 'POST',
          body: pendingInvite
            ? { email, password, name, inviteToken: pendingInvite.token }
            : { email, password, name, key: pendingKey },
        });
        if (!reg.ok) {
          // ⚠️ A spent key is its own case: the account already exists, so the honest move is to
          // put them on the sign-in door rather than repeat "could not create".
          if (reg.status === 409) {
            // ⚠️ Two different 409s now: a spent credential (sign in instead) and an invitation
            // bound to another address (nothing to do here but say so).
            if (reg.data.code === 'EMAIL_MISMATCH') {
              setError(reg.data.error);
              return;
            }
            setError(pendingInvite
              ? 'That invitation has already been used to set up an account. Please sign in.'
              : 'That key has already been used to set up an account. Please sign in.');
            setMode('signin');
            return;
          }
          setError(reg.data.error || 'Could not create the account.');
          return;
        }
        keyConsumed = true;
      }
      const r = await call('/login', { method: 'POST', body: { email, password } });
      if (!r.ok || !r.data.token) {
        setError(creating
          ? 'The account was made but signing in failed. Try signing in.'
          : 'That email and password did not match.');
        if (creating) setMode('signin');
        return;
      }
      onAuthed(r.data.token, r.data.title || name || null, { keyConsumed });
    } catch (_) {
      setError('Could not reach the server. Check your connection and try again.');
    } finally { setBusy(false); }
  };

  return (
    <form className="lp-card" onSubmit={submit} autoComplete="on">
      <h1 className="lp-prompt">{creating ? 'Create your account' : 'Sign in'}</h1>
      <p className="lp-muted">
        {creating
          ? 'So your books are here next time, without the code.'
          : 'Welcome back. Your books are waiting.'}
      </p>
      {pendingKey && (
        <p className="lp-notice">
          {keyLabel ? <>Key accepted — <strong>{keyLabel}</strong>. </> : <>Key accepted. </>}
          It will be added once you&rsquo;re {creating ? 'set up' : 'signed in'}.
        </p>
      )}

      {/*
        ⭐ NAME THE BOOKS. An invitation that says only "you have been invited" is indistinguishable
        from a phishing attempt; the titles are what make it credible, and the reader was sent them
        in the email anyway.
      */}
      {pendingInvite && (
        <p className="lp-notice">
          {pendingInvite.titles && pendingInvite.titles.length
            ? <>Waiting for you: <strong>{pendingInvite.titles.join(", ")}</strong>. </>
            : null}
          {pendingInvite.groups && pendingInvite.groups.length
            ? <>Everything in <strong>{pendingInvite.groups.join(", ")}</strong>, including books added later. </>
            : null}
          {!(pendingInvite.titles || []).length && !(pendingInvite.groups || []).length
            ? <>Your invitation is accepted. </> : null}
          It will be added once you&rsquo;re {creating ? 'set up' : 'signed in'}.
        </p>
      )}

      {/*
        ⛔ AN ACCOUNT CANNOT BE MADE WITHOUT A KEY, so say that instead of letting them fill the
        form and collect a 400. This is reachable: a reader who came in through "Sign in" and then
        chose "Create account" has no key in hand.

        ⚠️ Said plainly rather than apologetically — needing an invitation is the design, not a
        failure. The button goes back to the key box, which is where the invitation is entered.
      */}
      {creating && !invited && (
        <p className="lp-notice">
          You&rsquo;ll need the access key you were sent to set up an account.{' '}
          {onBack && (
            <button type="button" className="lp-link" onClick={onBack}>
              Enter your key
            </button>
          )}
        </p>
      )}

      <label htmlFor="lp-email" className="lp-visually-hidden">Email</label>
      {/* ⚠️ Locked when the invitation named an address — the server would refuse any other. */}
      <input id="lp-email" className="lp-input" type="email" value={email} placeholder="you@example.com"
        onChange={(e) => setEmail(e.target.value)} autoComplete="email" enterKeyHint="next"
        readOnly={!!boundEmail} />

      {creating && (
        <>
          <label htmlFor="lp-name" className="lp-visually-hidden">Your name</label>
          <input id="lp-name" className="lp-input" value={name} placeholder="Your name"
            onChange={(e) => setName(e.target.value)} autoComplete="name" enterKeyHint="next" />
        </>
      )}

      <label htmlFor="lp-pw" className="lp-visually-hidden">Password</label>
      <input id="lp-pw" className="lp-input" type="password" value={password}
        placeholder={creating ? 'A password (8 characters or more)' : 'Password'}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete={creating ? 'new-password' : 'current-password'} enterKeyHint="go" />

      {error && <p className="lp-error" role="alert">{error}</p>}
      {/* ⓘ `creating && !pendingKey` is disabled rather than hidden: the notice above explains why,
          and a vanished button reads as a broken page. */}
      <button className="lp-button lp-primary" type="submit"
        disabled={busy || !email.trim() || !password
          || (creating && (!name.trim() || !invited))}>
        {busy ? 'Just a moment…' : (creating ? 'Create account' : 'Sign in')}
      </button>

      {/* ⚠️ Always both doors. The server's status is a default, and a returning reader with a new
          key would otherwise be sent to create a second account. */}
      <p className="lp-help lp-small">
        {creating ? 'Already have an account? ' : 'First time here? '}
        <button type="button" className="lp-link" onClick={() => setMode(creating ? 'signin' : 'create')}>
          {creating ? 'Sign in instead' : 'Create account'}
        </button>
      </p>

      {/*
        ⓘ A way back, and only when there is one to offer. `onBack` is passed only when the reader
        reached this card by choosing "Sign in" from the key box — with a key in hand there is
        nothing to go back TO, since the key box has already done its job.

        ⚠️ Not a browser Back: this is one route with internal state, so Back would leave the page
        entirely and lose the pending key.
      */}
      {onBack && !pendingKey && (
        <p className="lp-help lp-small">
          <button type="button" className="lp-link" onClick={onBack}>
            I have an access key instead
          </button>
        </p>
      )}
    </form>
  );
}

/**
 * ⚠️ HOW LONG "Download started." STAYS UP, and why it has to come down at all.
 *
 * ⛔ The page CANNOT know when a browser download finishes. `window.location.href = url` hands the
 * transfer to the browser and there is no completion event to listen for — so "started" is the only
 * honest thing this row can ever say.
 *
 * It said it permanently, which the operator read as a stuck card: *"I have a download history and
 * file, but the card still reads 'Download started'."* By then it was describing something that had
 * finished minutes earlier, and while it showed, the "Large file — Wi-Fi recommended" hint could
 * never come back.
 *
 * So the message is a NOTICE, not a state: long enough to read, then the row returns to idle and is
 * plainly ready to be used again. Six seconds — a confirmation nobody has to dismiss.
 */
const STARTED_NOTICE_MS = 6000;

/**
 * ⓘ `compact` is for a chapter inside a book row, where the title is already said above it.
 * Everything else about the row — the pinned button width, the large-file note, the "Download
 * started" line that is allowed to reflow — is unchanged and documented below.
 */
function FileRow({ item, token, onExpired, compact = false, label, hint }) {
  const [state, setState] = useState('idle');   // idle | preparing | started | error
  const [error, setError] = useState(null);

  /**
   * ⚠️ Cleared on unmount as well as on change, or a client who navigates away mid-notice gets a
   * setState on an unmounted component — a warning in dev and a leak in principle.
   */
  useEffect(() => {
    if (state !== 'started') return undefined;
    const id = setTimeout(() => setState('idle'), STARTED_NOTICE_MS);
    return () => clearTimeout(id);
  }, [state]);

  const download = async () => {
    setState('preparing'); setError(null);
    try {
      const r = await call(`/listen/file/${encodeURIComponent(item.uuid)}`, { token });
      if (r.status === 401) { onExpired(); return; }
      if (!r.ok || !r.data.url) throw new Error(r.data.error || 'That file is not available.');
      // Same-window navigation to an attachment URL: the page stays put and the browser downloads.
      window.location.href = r.data.url;
      setState('started');
    } catch (e) {
      setError(e.message || 'Could not start the download.');
      setState('error');
    }
  };

  const meta = [fmtDuration(item.durationMs), fmtSize(item.audioBytes)].filter(Boolean).join(' · ');
  /**
   * ⭐ COMPACT IS THE BUTTON AND NOTHING ELSE (operator, 2026-10-23): *"remove the redundant
   * title, time, size and Large file comment from the second column."* The row head above it
   * already says the title and "10h 41m · 880 MB", so repeating them beside the button was the
   * bulk the grid was meant to remove.
   *
   * ⛔ A <div>, NOT AN <li>. In compact form this sits in the row head rather than in a list, and
   * an <li> with no list around it is invalid markup that a screen reader reads as a list of one.
   *
   * ⓘ WHAT SURVIVES, because neither is redundant: "Download started." and an error. Both are new
   * information arriving, and the row head cannot say either.
   */
  if (compact) {
    return (
      <div className="lp-file-compact">
        {/*
          ⓘ `title` is the hover hint, and terse on purpose: somebody hovering a download button
          wants to know which file this is, not to read a paragraph.

          ⚠️ IT IS A SUPPLEMENT, NEVER THE ONLY PLACE SOMETHING IS SAID. A touch screen has no
          hover at all, so the label and the row’s own meta line still have to carry the decision —
          which is most of the readers, since this page is built for a phone.
        */}
        <button className="lp-button lp-button-sm" type="button" onClick={download}
          disabled={state === 'preparing'} title={hint || undefined}
          aria-label={`Download ${item.title || item.fileName}`}>
          {state === 'preparing' ? '…' : (label || 'Download')}
        </button>
        {state === 'started' && <div className="lp-ok lp-small lp-file-state">Download started.</div>}
        {error && <div className="lp-error lp-small lp-file-state">{error}</div>}
      </div>
    );
  }

  return (
    <li className="lp-file">
      <div className="lp-file-text">
        <div className="lp-file-title">{item.title || item.fileName}</div>
        {meta && <div className="lp-muted lp-small">{meta}</div>}
        {/*
          ⛔ NOT `state === 'idle'` ANY MORE. Gating this on idle made it VANISH the instant the
          button was clicked, which was one of three layout shifts inside the ~100ms of the signed-URL
          request — operator: *"the card renders some text? Or pushes something down... in either case
          it is too fast to read."* The sentence is true whatever the row is doing: a 100 MB+ file is
          still a 100 MB+ file while it downloads. So it stays, and the row stops jumping.
        */}
        {item.audioBytes > LARGE_FILE && (
          <div className="lp-muted lp-small">Large file — Wi-Fi recommended.</div>
        )}
        {state === 'started' && <div className="lp-ok lp-small">Download started.</div>}
        {error && <div className="lp-error lp-small">{error}</div>}
      </div>
      {/*
        ⚠️ The label changes from "Download" to "…", and without a floor on the width the button
        collapses from ~117px to its 44px minimum and the text column springs sideways — the second
        of the three shifts. Pinned in CSS (`.lp-file .lp-button`) rather than by padding the glyph,
        so the two labels occupy one box.

        ⓘ The third shift — "Download started." appearing — is left alone. That one is new
        information arriving, which is the one thing on this row that has earned a reflow.
      */}
      <button className="lp-button lp-button-sm" type="button" onClick={download}
        disabled={state === 'preparing'}
        aria-label={`Download ${item.title || item.fileName}`}>
        {state === 'preparing' ? '…' : (label || 'Download')}
      </button>
    </li>
  );
}

/**
 * NotesBlock — what this reader found in one book.
 *
 * ⭐ THE WHOLE FEATURE IS A PLACE TO PUT FINDINGS. Six readers have the books for quality
 * control, and the alternative is what the operator named: *"Otherwise, I’ll get email and texts
 * or pictures."* So the bar is not a QA tool — it is a table that accumulates: book, where, what.
 *
 * ⛔ BOTH FIELDS ARE FREE TEXT, BY DECISION — *"It can be free form data."* A reader is listening
 * in the Files app with a chapter title and a running clock, and no two of them will write that the
 * same way: "ch 3, 14:22", "about 3 min in", "the bit with the hovertank". A structured
 * chapter+offset control would reject most of those or make them translate, and a rejected note
 * goes back to being a text message. Nothing here parses or validates; the server caps length.
 *
 * ⓘ AND THE TIME MUST BE TYPED, because this page does not stream — it hands over a file that
 * plays in the phone’s Files app, so the app genuinely cannot know where anybody is in the book.
 * That is the fact that decided the design, not a shortcut.
 *
 * ⚠️ INLINE, NOT A MODAL. The page is plain cards and inline forms (KeyForm is one), and a modal
 * on a phone covers the very thing being described. ⓘ A note is also the reader’s own, so Remove
 * asks nothing: it is one line of their own text, and ConfirmModal is for consequences.
 *
 * ⛔ A READER SEES ONLY THEIR OWN NOTES, here and in the API. Six independent observations are the
 * value of six readers; one visible opinion turns the next five into agreement.
 */
const NotesBlock = ({ release, notes, token, onExpired, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [whereText, setWhereText] = useState('');
  const [whatText, setWhatText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);
  /**
   * ⭐ COLLAPSED BY DEFAULT, because the list only grows. The operator: *"the card based display
   * is too bulky and will become more cumbersome as users add notes to books."* A reader writing
   * their eighth note should not have to scroll past the first seven to reach the form.
   */
  const [showNotes, setShowNotes] = useState(false);

  const mine = notes.filter((n) => n.releaseUuid === release.uuid);

  const save = async (e) => {
    e.preventDefault();
    const what = whatText.trim();
    if (!what) { setError('Say what you found.'); return; }
    setSaving(true);
    setError(null);
    // ⓘ The chapter is PREPENDED to `where` rather than stored separately: the panel reads one
    // free-text column, and a second field would have to be threaded through the service, the
    // graph and the admin table to show the same sentence.
    const where = [chapter, whereText.trim()].filter(Boolean).join(' — ');
    const r = await call('/listen/my/notes', {
      method: 'POST',
      token,
      body: { releaseUuid: release.uuid, whereText: where, whatText: what },
    });
    setSaving(false);
    if (r.status === 401) { onExpired(); return; }
    if (!r.ok) { setError(r.data.error || 'Could not save that note.'); return; }
    setWhereText('');
    setChapter('');
    setWhatText('');
    setOpen(false);
    await onChanged();
  };

  const remove = async (uuid) => {
    setRemoving(uuid);
    const r = await call('/listen/my/notes/' + uuid, { method: 'DELETE', token });
    setRemoving(null);
    if (r.status === 401) { onExpired(); return; }
    if (r.ok) await onChanged();
  };

  /**
   * ⭐ THE CHAPTERS, FETCHED ONLY WHEN THE FORM OPENS. A reader who never writes a note should not
   * pay for a list they will not see, and a shelf of six books would otherwise fire six requests on
   * load for nothing.
   *
   * ⛔ THE FREE-TEXT FIELD STAYS. The dropdown names the chapter; `where` still carries the part a
   * dropdown cannot — "about 3 minutes in", "the bit with the hovertank". The operator settled that
   * on 2026-10-23 ("It can be free form data") and this does not reopen it: it removes the
   * transcription, not the sentence.
   */
  const [chapters, setChapters] = useState([]);
  const [chapter, setChapter] = useState('');

  useEffect(() => {
    if (!open || chapters.length) return undefined;
    let cancelled = false;
    (async () => {
      const r = await call('/listen/my/releases/' + encodeURIComponent(release.uuid) + '/chapters', { token });
      // ⓘ Silent on failure: a note form without a chapter list still works.
      if (!cancelled && r.ok) setChapters(r.data.chapters || []);
    })();
    return () => { cancelled = true; };
  }, [open, chapters.length, release.uuid, token]);

  const whereId = 'lp-where-' + release.uuid;
  // ⓘ The toggle now PRECEDES the list, which is the disclosure pattern a screen reader
  // expects — and it needs `aria-controls` pointing at the thing it opens.
  const listId = 'lp-notes-' + release.uuid;
  const whatId = 'lp-what-' + release.uuid;

  return (
    <div className="lp-notes">
      {/*
        ⭐ ABOVE THE LIST, AND IT DOES NOT MOVE (operator, 2026-10-23): *"I’d like to have the
        ‘expand note list’ button above first note in a book’s list of notes… but it should stay in
        one place."* It used to render AFTER the list, so opening three notes pushed the control
        down past them and the reader had to hunt for it to close them again.

        ⓘ THE COUNT IS STILL THE AFFORDANCE. A bare triangle gives a reader no reason to open
        anything; "3 notes" does, and it answers the question most of them were opening it to ask.
        ⚠️ So it is smaller rather than reduced to an icon — 0.8em and tight — because the number
        is the part worth keeping.
      */}
      {mine.length > 0 && (
        <p className="lp-note-toggle">
          <button type="button" className="lp-link lp-note-toggle-btn"
            onClick={() => setShowNotes(!showNotes)}
            aria-expanded={showNotes} aria-controls={listId}>
            <span className="lp-caret" aria-hidden="true">{showNotes ? '▾' : '▸'}</span>
            {mine.length} {mine.length === 1 ? 'note' : 'notes'}
          </button>
        </p>
      )}

      {mine.length > 0 && showNotes && (
        <ul id={listId} className="lp-note-list">

          {mine.map((n) => (

            <li key={n.uuid} className="lp-note">
              {/*
                ⭐ ONE BLOCK, A LINE BREAK BETWEEN THEM (operator, 2026-10-23). Two paragraphs meant
                two margins and two line boxes for what is one finding; now the place is the first
                line of the note and the finding is the rest.

                ⓘ JSX drops whitespace-only lines, so the indentation here does not land in the
                text — which matters because this element is `pre-wrap`: a reader’s own line breaks
                are part of what they wrote, and a stray source newline would be too.
              */}
              <p className="lp-note-what">
                <span className="lp-note-place lp-muted lp-small">{n.whereText || 'no place given'}</span>
                <br />
                {n.whatText}
              </p>

              {/* ⓘ Beside the block, not inside it: a button in a `pre-wrap` paragraph inherits the
                  whitespace handling and wraps in odd places. */}
              <button type="button" className="lp-link lp-note-remove" onClick={() => remove(n.uuid)}
                disabled={removing === n.uuid}>
                {removing === n.uuid ? '…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {open ? (
        <form className="lp-note-form" onSubmit={save}>
          {/*
            ⭐ THE LABELS ARE STILL HERE, THEIR LINES ARE NOT. Two visible label lines cost about
            40px per open row for four words a placeholder can say — and the operator asked for the
            vertical. `.lp-visually-hidden` is the page’s existing answer for this; the account form
            uses it for every field.

            ⛔ NOT `placeholder` ALONE. A placeholder is not a label: it vanishes the moment anybody
            types, screen readers treat it inconsistently, and a form whose fields lose their names
            when filled is unusable for the reader who most needs them. The label stays in the
            markup and the hint moves INTO the placeholder, so each field says what it is twice
            while costing one line.
          */}
          {/* ⓘ Only when there are chapters to offer — an older release derives none. */}
          {chapters.length > 0 && (
            <>
              <label className="lp-visually-hidden" htmlFor={whereId + '-ch'}>Chapter</label>
              <select id={whereId + '-ch'} className="lp-text lp-note-chapter" value={chapter}
                onChange={(e) => setChapter(e.target.value)}>
                <option value="">Which chapter? (optional)</option>
                {chapters.map((c) => (
                  <option key={c.number} value={c.title}>{c.title} · {c.start}</option>
                ))}
              </select>
            </>
          )}
          <label className="lp-visually-hidden" htmlFor={whereId}>Where</label>
          <input id={whereId} className="lp-text" type="text" value={whereText}
            onChange={(e) => setWhereText(e.target.value)} autoComplete="off"
            placeholder={chapters.length ? 'Where in it — about 14:20' : 'Where — chapter 3, about 14:20'} />
          <label className="lp-visually-hidden" htmlFor={whatId}>What you found</label>
          {/* ⓘ Two rows, not three: the field grows as somebody types past them. */}
          {/* ⓘ `aria-invalid` is what a screen reader reads AND what the error style keys on, so
              the announcement and the red border cannot drift apart. */}
          <textarea id={whatId} className="lp-text lp-note-area" rows={2} value={whatText}
            aria-invalid={error ? 'true' : undefined}
            onChange={(e) => setWhatText(e.target.value)}
            placeholder="What you found — Arrowmere should rhyme with mere, not merry." />
          {error && <p className="lp-error lp-small" role="alert">{error}</p>}
          <div className="lp-note-actions">
            <button className="lp-button lp-button-sm" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save note'}
            </button>
            <button className="lp-link" type="button"
              onClick={() => { setOpen(false); setError(null); }}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <p className="lp-help">
          <button type="button" className="lp-link" onClick={() => setOpen(true)}>
            {mine.length ? 'Add another note' : 'Add a note about this book'}
          </button>
        </p>
      )}
    </div>
  );
};

/**
 * The cover in a shelf row.
 *
 * ⚠️ TWO URLS, AND THE SECOND IS THE NET. The server signs the `thumb` derivative and the
 * original; a cover whose derivative was never generated signs fine and then 404s, so `onError`
 * swaps to the original rather than leaving a broken image. Same two-step the Marketplace card
 * uses, for the same reason.
 *
 * ⓘ `loading="lazy"` and the fixed box: the box is sized in CSS so the row never reflows when
 * the picture arrives.
 */
function CoverThumb({ release }) {
  const [src, setSrc] = useState(release.cover || release.coverFallback);
  const [fellBack, setFellBack] = useState(false);

  const onError = () => {
    if (fellBack || !release.coverFallback) return;
    setFellBack(true);
    setSrc(release.coverFallback);
  };

  if (!src) return <div className="lp-cover-blank" aria-hidden="true" />;
  return <img className="lp-cover" src={src} alt="" loading="lazy" onError={onError} />;
}

/**
 * The one line under a book title: how long it is and how big the download is.
 *
 * ⓘ Summed across items so a `chapters` release reads as the whole book rather than as its first
 * file. ⓘ `durationMs` here is the real audio length — it comes from :RenderedChapter through the
 * release item, not from :RenderedLine, where the same field name means request latency.
 */
/**
 * ⓘ A release can hold BOTH formats of one book: the chapterised m4b audiobook, and plain audio
 * — either one whole-book MP3 or one MP3 per chapter. Told apart by extension, because that is
 * what distinguishes them to a reader and to their player.
 */
const isAudiobook = (item) => /\.m4b$/i.test(String((item && item.fileName) || ''));
const audiobookOf = (release) => (release.items || []).find(isAudiobook) || null;
const plainFilesOf = (release) => (release.items || []).filter((i) => !isAudiobook(i));

function shelfMeta(release) {
  // ⛔ The m4b is the SAME book, not more of it — counting both would double the hours and the
  // megabytes shown on the row.
  const items = plainFilesOf(release);
  const ms = items.reduce((a, i) => a + (i.durationMs || 0), 0);
  const bytes = items.reduce((a, i) => a + (i.audioBytes || 0), 0);
  const book = audiobookOf(release);
  // ⓘ Both sizes when both exist, the smaller first: it is the one we recommend.
  const parts = [fmtDuration(ms)];
  parts.push(book && book.audioBytes ? fmtSize(book.audioBytes) + ' / ' + fmtSize(bytes) : fmtSize(bytes));
  if (items.length > 1) parts.push(items.length + ' chapters');
  return parts.join(' · ');
}

/**
 * ⭐ THE GUIDE ARTICLE’S ID, in one place on the client as it is on the server.
 *
 * ⓘ `/guide/` is the chrome-free route — same `<ArticleItem />` as `/articleItemPage/`, but a
 * top-level sibling of "/", so a reader following it gets the article and its own header rather
 * than the app’s navbar and a sign-in prompt. `?from=/listen` is what gives the article a way back.
 */
const GUIDE_UUID = 'b7e41a92-5c38-4d17-9a6e-2f8c0d5b71e4';
const GUIDE_URL = '/guide/' + GUIDE_UUID + '?from=/listen';

/**
 * The two or three steps that matter on ONE platform.
 *
 * ⛔ SHORT ON PURPOSE. This used to be a Modal holding every platform, which obliterated the
 * shelf; the operator asked for a popover and the full version as an article. So this is the
 * minimum a reader needs to act — and the article is one tap away for anyone who wants the rest.
 */
const STEPS = {
  ios: [
    'Tap Audiobook — it saves to Files › Downloads.',
    'Open Files, tap the file, then Share › Books.',
    'It plays in Books, with chapters and your place kept.',
  ],
  mac: [
    'Download Audiobook.',
    'Double-click it — Apple Books imports it.',
    'It appears under Audiobooks, chapters and all.',
  ],
  windows: [
    'Install iTunes (not the Apple Music app).',
    'Download Audiobook, then File › Add File to Library.',
    'It appears under Audiobooks. ⛔ Windows Media Player will not open it.',
  ],
  android: [
    'Install an audiobook app — Voice or Smart AudioBook Player.',
    'Download Audiobook and open it there.',
    'Or take the MP3, which plays in anything.',
  ],
  other: [
    'Take the Audiobook if your player opens .m4b files.',
    'Otherwise take the MP3 — it plays anywhere.',
  ],
};

const ListenPage = () => {
  const [token, setToken] = useState(() => store.get());
  const [name, setName] = useState(() => store.getName());
  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  /**
   * The key a reader has just proved valid but has nowhere to put yet.
   *
   * ⭐ IN MEMORY ONLY, never stored. It survives the auth step and is redeemed the moment there is
   * an account, then dropped. Putting it in storage would leave a working credential on the device
   * for no benefit — the entitlement it buys is on the account within a second of arriving.
   */
  const [pendingKey, setPendingKey] = useState(null);
  const [keyLabel, setKeyLabel] = useState(null);
  const [mode, setMode] = useState('create');   // which door key-status suggested

  /**
   * ⭐ THE INVITATION IN THE URL — `/listen?invite=<token>`.
   *
   * ⛔ A ROUTE OF ITS OWN WOULD BE A SECOND COPY of the register-or-sign-in decision this page
   * already makes, and readers would land on the wrong one half the time.
   */
  const [pendingInvite, setPendingInvite] = useState(null);

  // ⓘ Which multi-item release has its chapter list open. One at a time, by uuid.
  const [openChapters, setOpenChapters] = useState(null);

  // ⓘ The "which download?" explanation, shut by default. Shelf-level: the answer is the same
  // for every book.
  const [showFormats, setShowFormats] = useState(false);

  /**
   * ⓘ Read once, not on every render: a user agent does not change mid-visit, and this only picks
   * a DEFAULT — a wrong guess costs one tap on the link to the full guide.
   */
  const [platform] = useState(() => detectPlatform());
  const [addingKey, setAddingKey] = useState(false);  // a signed-in reader adding another book

  /**
   * ⭐ THE RETURNING READER, with no key and no need of one.
   *
   * This is the second entrance, and it is the one that makes the account worth having. The page
   * used to have exactly one way in — a key that `key-status` accepted — so a reader who had
   * registered, signed out, or opened a clean browser could not reach the sign-in form at all. Their
   * books were sitting on an account the page would not let them prove they owned.
   *
   * ⚠️ It is DELIBERATELY NOT a third card. The sign-in form already exists and already works
   * without a key; what was missing was a state that renders it on its own. Hence a flag beside
   * `pendingKey` rather than a new component: one form, two ways of arriving at it.
   */
  const [wantsSignIn, setWantsSignIn] = useState(false);

  /**
   * ⓘ THE READER’S OWN QC NOTES, loaded once for every book rather than per card. The set is
   * tens of rows for six readers across six books, so one request and a filter in NotesBlock is
   * both simpler and fewer round-trips than a fetch per release.
   */
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Your audiobook';
    // Not for search engines.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    return () => { document.title = prevTitle; robots.remove(); };
  }, []);

  const signOut = useCallback((message = null) => {
    store.clear();
    setToken(null);
    setName(null);
    setReleases(null);
    setNotes([]);
    setPendingInvite(null);
    setNotice(message);
    // ⚠️ Back to the key box, not to whichever card was last open. Leaving `wantsSignIn` set would
    // drop a reader who just signed out straight into a sign-in form, which reads as a rejection.
    setWantsSignIn(false);
    setAddingKey(false);
  }, []);

  const expired = useCallback(
    // ⓘ Now a real session rather than a key, so the honest cause is the sign-in lapsing. A
    // withdrawn ENTITLEMENT no longer logs anybody out — that book simply stops being listed.
    () => signOut('Your sign-in has expired. Please sign in again.'),
    [signOut],
  );

  const load = useCallback(async (t) => {
    setError(null);
    try {
      const r = await call('/listen/my/releases', { token: t });
      if (r.status === 401) { expired(); return; }
      /**
       * ⚠️ 403 IS A REAL ARRIVAL NOW THAT ANYONE CAN SIGN IN HERE. `/api/login` accepts any
       * account, so a book creator or voice actor typing their own credentials into this page gets
       * a valid token and then a flat refusal from `my/releases` — the role check, working
       * correctly. Without this they would sit on an empty library holding a token that can never
       * load anything, with only "Sign out" to guess at. So the token is dropped and the reason
       * said plainly, back at the key box where they can do something about it.
       */
      if (r.status === 403) {
        signOut('That account is not set up for listening. Enter an access key to get started.');
        return;
      }
      if (!r.ok) { setError(r.data.error || 'Could not load your downloads.'); return; }
      setReleases(r.data.releases || []);
    } catch (_) {
      setError('Could not reach the server. Check your connection and try again.');
    }
  }, [expired, signOut]);

  /**
   * ⛔ A FAILED NOTES READ IS SILENT. The notes are an addition to this page, not its purpose:
   * a reader who cannot load them must still get their books, so there is no error state here
   * and nothing calls setError. An expired session is still handled, by the releases load.
   */
  const loadNotes = useCallback(async (t) => {
    const r = await call('/listen/my/notes', { token: t });
    if (r.ok) setNotes(r.data.notes || []);
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      if (!cancelled) await load(token);
      if (!cancelled) await loadNotes(token);
    })();
    return () => { cancelled = true; };
  }, [token, load, loadNotes]);

  /**
   * Redeem whatever key is in hand against the signed-in account.
   *
   * ⭐ SAYS SOMETHING WHEN NOTHING CHANGED. A re-issued key for a book the reader already has
   * refreshes the expiry and adds no rows — which the operator's own 21 keys show is the common
   * case (ShonniMj -> RealShonniMj -> ShonniMJGood -> ShonnyMJFinal). Silence after typing a code
   * reads as broken, so "already yours, access extended" is said out loud.
   */
  const redeem = useCallback(async (t, rawKey) => {
    const r = await call('/listen/redeem', { method: 'POST', body: { key: rawKey }, token: t });
    if (r.status === 401) { expired(); return; }
    if (!r.ok) { setError(r.data.error || 'That key could not be added.'); return; }
    setNotice(r.data.added
      ? `Added ${r.data.added} ${r.data.added === 1 ? 'book' : 'books'}.`
      : 'That was already yours — access extended.');
    await load(t);
  }, [expired, load]);

  /**
   * Claim an invitation against the signed-in account.
   *
   * ⓘ The twin of `redeem`, and separate for the same reason the endpoints are: one spends a
   * credential a human typed, the other a token a machine generated.
   */
  const redeemInvite = useCallback(async (t, inviteToken) => {
    const r = await call('/listen/invite/redeem', {
      method: 'POST', body: { token: inviteToken }, token: t,
    });
    if (r.status === 401) { expired(); return; }
    // ⓘ 409 EMAIL_MISMATCH is not a failure to retry: the invitation belongs to somebody else,
    // and the server names the address it was sent to so the reader can act on it.
    if (!r.ok) { setError(r.data.error || 'That invitation could not be added.'); return; }
    const books = r.data.added || 0;
    const groups = r.data.collections || 0;
    // ⓘ Built as parts and joined: an invitation can carry books, collections or both, and the
    // nested-ternary version of this sentence was unreadable.
    const parts = [];
    if (books) parts.push(`${books} ${books === 1 ? 'book' : 'books'}`);
    if (groups) parts.push(`${groups} ${groups === 1 ? 'collection' : 'collections'}`);
    setNotice(parts.length
      ? `Added ${parts.join(' and ')}.`
      : 'That was already yours — access extended.');
    await load(t);
  }, [expired, load]);

  /**
   * ⭐ READ THE INVITATION, THEN TAKE IT OUT OF THE ADDRESS BAR.
   *
   * ⛔ `replaceState` IS NOT COSMETIC. A token left in the URL is a live credential in the
   * address bar: it goes into history, into a screenshot, into whatever the reader pastes when
   * they ask for help, and into the Referer header of anything this page links out to. It is
   * single-use and revocable, but the cheapest moment to stop carrying it is immediately.
   *
   * ⓘ Already signed in? Claim it now. Otherwise hold it until the form has done its work.
   */
  useEffect(() => {
    let token0 = null;
    try {
      token0 = new URLSearchParams(window.location.search).get('invite');
    } catch (_) { /* no URL API, nothing to claim */ }
    if (!token0) return;

    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch (_) { /* a page that cannot rewrite its URL still works */ }

    let cancelled = false;
    (async () => {
      const r = await call('/listen/invite-status', { method: 'POST', body: { token: token0 } });
      if (cancelled) return;
      if (!r.ok || r.data.status === 'invalid') {
        setNotice('That invitation link is no longer valid. Please ask for a new one.');
        return;
      }
      const info = {
        token: token0,
        email: r.data.email || null,
        label: r.data.label || null,
        titles: r.data.titles || [],
        groups: r.data.groups || [],
      };
      // ⓘ A reader who is already signed in on this device never sees a form.
      const existing = store.get();
      if (existing) { await redeemInvite(existing, token0); return; }
      setPendingInvite(info);
      setMode(r.data.status === 'needs-signin' ? 'signin' : 'create');
    })();
    return () => { cancelled = true; };
  }, [redeemInvite]);

  /** A valid key, with nowhere to put it yet. Held in memory; the door comes from key-status. */
  const onKey = (rawKey, status, label) => {
    setError(null); setNotice(null);
    setKeyLabel(label);
    if (token) {
      // Already signed in and adding another book — redeem straight away.
      setAddingKey(false);
      redeem(token, rawKey);
      return;
    }
    setPendingKey(rawKey);
    setMode(status === 'needs-signin' ? 'signin' : 'create');
  };

  /** The returning reader's door: the sign-in form, with no key involved. */
  const onWantSignIn = () => {
    setError(null); setNotice(null);
    setMode('signin');
    setWantsSignIn(true);
  };

  /** Back out of that door to the key box. */
  const onBackToKey = () => {
    setError(null); setNotice(null);
    setWantsSignIn(false);
  };

  /**
   * Signed in. If a key is still waiting, spend it now and then show the library.
   *
   * ⓘ `keyConsumed` is set when SIGNUP already redeemed it, which it does because registration now
   * requires the key. Redeeming again would work — it is idempotent — but it would tell a reader who
   * just created their account that the book was "already yours, access extended".
   */
  const onAuthed = async (t, displayName, { keyConsumed = false } = {}) => {
    store.set(t); store.setName(displayName);
    setNotice(null); setError(null);
    setName(displayName || null);
    setToken(t);
    setWantsSignIn(false);
    if (pendingKey && !keyConsumed) {
      const k = pendingKey;
      setPendingKey(null); setKeyLabel(null);
      await redeem(t, k);
      return;
    }
    /**
     * ⓘ The same shape for an invitation: signup already claimed it (`keyConsumed`), so this is
     * the SIGN-IN path — a reader who already had an account and followed a link.
     */
    if (pendingInvite && !keyConsumed) {
      const inv = pendingInvite;
      setPendingInvite(null);
      await redeemInvite(t, inv.token);
      return;
    }
    setPendingKey(null); setKeyLabel(null); setPendingInvite(null);
  };

  return (
    <div className="navBarHeaderContainer">
      <div className="listen-page">
        <div className="logoContainer">
          {/* Named so the header can be laid out on its own terms. It is the one LEFT-aligned
              block on the page — everything below it is centred — and it shares the content
              width so the logo lines up with the card edges rather than the window edge. */}
          <div className="listen-brand">
            <img className="headerLogo" src={logo} alt="Authorator-ai" />
            {/* ⓘ No <br /> any more: `.headNameContainer` is a flex column here, so the three lines
                stack and share a left edge. The <br /> did nothing while the spans were floated,
                which is why they sat side by side. */}
            <div className="headNameContainer">
              <span className="headAppName">Authorator-ai</span>
              <span className="headAppSubTitle">AI Narration Accelerator</span>
              {/* The key's label — there is no account, so this is the only name the page has.
                  ⚠️ The inline `marginLeft: 30px` is gone rather than overridden: an inline style
                  beats any selector, so it could not be fixed from the stylesheet. */}
              {name && <span className="headAppSubTitle">{name}</span>}
            </div>
          </div>
          <HeroImages />

        </div>
        {/* </div> */}
        <main className="lp-main">
          {/*
            ⭐ FOUR STATES, where there used to be two. The path through them is: key box ->
            create-or-sign-in (the key waiting in memory) -> library. A returning reader lands
            straight in the library, which is the point of the account.

            ⛔ AND TWO WAYS TO REACH THE MIDDLE ONE, which is the fix here. `pendingKey` is the
            arriving reader; `wantsSignIn` is the returning one. The form rendered is the same, and
            it already worked without a key — the bug was that nothing could get to it that way, so
            a reader who signed out or opened a clean browser was locked out of their own account by
            a page that would only talk to a key.
          */}
          {!token && !pendingKey && !pendingInvite && !wantsSignIn && (
            <KeyForm onKey={onKey} notice={notice} onWantSignIn={onWantSignIn} />
          )}
          {!token && (pendingKey || pendingInvite || wantsSignIn) && (
            <AccountForm mode={mode} setMode={setMode} pendingKey={pendingKey}
              keyLabel={keyLabel} pendingInvite={pendingInvite} onAuthed={onAuthed}
              onBack={wantsSignIn ? onBackToKey : null} />
          )}

          {token && (
            <>
              <header className="lp-header">
                <h1 className='lp-prompt'>Your downloads</h1>
                <button className="lp-link" type="button" onClick={() => signOut()}
                  title="Sign out on this device. Your books stay on your account.">
                  Sign out
                </button>
              </header>

              {error && <p className="lp-error" role="alert">{error}</p>}
              {notice && <p className="lp-notice">{notice}</p>}
              {!releases && !error && <p className="lp-muted">Loading…</p>}
              {releases && releases.length === 0 && (
                <p className="lp-card lp-muted">
                  Nothing here yet. Add an access key below when you have one.
                </p>
              )}

              {/*
                ⭐⭐ A GRID OF BOOKS, NOT A STACK OF CARDS (operator, 2026-10-23): *"the card based
                display is too bulky and will become more cumbersome as users add notes."*

                Two cells per book. Left: the cover. Right: the title, the duration and size, a
                download button top-right, the note form under it, and the previous notes behind a
                collapse.

                ⛔ A REAL <table> WAS THE WRONG ANSWER. Readers are on phones — /listen is a
                download page played from the Files app — and a table cannot reflow: at 390 px the
                second column would hold a form inside a cell that cannot wrap below the first. CSS
                grid gives the same two-column alignment AND collapses to one column, which is why
                `.lp-row` is a grid rather than markup pretending to be one.

                ⓘ The cover is the `thumb` derivative, signed server-side — 14 KB against a 4.7 MB
                original. It is signed by the server because the cover lives under the OWNER’s
                prefix and the reader is not the owner; see coverThumb.js.
              */}
              {releases && !!releases.length && (
                <div className="lp-shelf">
                  {/*
                ⭐ ONCE, AT THE TOP OF THE LIST (operator, 2026-10-23). The same sentence applied to
                every book, so per-row it was noise — and it is advice a reader needs BEFORE tapping
                a download rather than after scrolling past six of them.

                ⚠️ Still INSIDE the shelf surface: on the page wash `--lp-muted` is 3.33:1, which is
                why this line lived in a card in the first place (2026-10-12).
              */}
                  <p className="lp-muted lp-small lp-shelf-help" >
                    {/*
                      ⛔ THE SENTENCE IS ONE FLEX ITEM, AND IT HAS TO BE SAID SO. A flex container makes
                      an item of every child INCLUDING each run of text, so this paragraph became four:
                      the words before <strong>, the <strong> itself, the words after, and the button —
                      and  spread them evenly, which read as a three-column table.
                      Wrapping the prose in a span puts it back to two items: sentence, then button.
                    */}
                    <span>
                      On iPhone, downloads are saved to <strong>Files › Downloads</strong>. Open the Files
                      app and tap a file to play it.
                    </span>
                    {/*
                      ⭐ A TAPPABLE HINT, BECAUSE HOVER DOES NOT EXIST ON A PHONE. The two download
                      buttons carry `title` tooltips, which a laptop shows and a touch screen simply
                      never offers — and most of these readers are on a phone. So the same explanation
                      has a control anyone can reach.

                      ⓘ ONCE, HERE, not per row: the answer is identical for all six books, and six
                      copies of it is the repetition the download hint was already moved out of.

                      ⛔ A DISCLOSURE, NOT A DIALOG. `window.alert` is barred in this project — the
                      policy is that a confirm or an alert becomes an in-app element — and a tooltip
                      library would be a dependency to say four lines. `aria-expanded` and
                      `aria-controls` make the real relationship legible to a screen reader.
                    */}
                    {' '}
                    {/*
                      ⭐ A POPOVER, NOT A MODAL (operator, 2026-10-25): *"the popover (not
                      obliterating modal) can have a link to the article."* A Modal held every
                      platform’s steps and covered the shelf to do it; this shows only the reader’s
                      own platform and leaves the books visible behind it.

                      ⚠️ `on="click"`, because a touch screen has no hover — which is the whole reason
                      this control exists rather than the buttons’ `title` tooltips alone.

                      ⛔ Semantic’s own components inside, as before: a Popup renders in a PORTAL at
                      body level, outside `.listen-page`, so no `--lp-*` token or `.lp-` rule reaches
                      in there.
                    */}
                    <Popup
                      on="click"
                      position="bottom right"
                      /**
                       * ⓘ `offset={[skidding, distance]}` — the popper array, used the same way by
                       * AllowlistBadge and bookNavigator. 10px of skidding puts the arrow over the
                       * middle of the icon: a `bottom right` popup aligns its EDGE to the trigger
                       * rather than its arrow, so without this the arrow sits left of centre on a
                       * 1.5em button.
                       */
                      offset={[10, 0]}
                      open={showFormats}
                      onOpen={() => setShowFormats(true)}
                      onClose={() => setShowFormats(false)}
                      style={{ maxWidth: 'min(22rem, 92vw)' }}
                      trigger={(
                        <button type="button" className="lp-hint-btn"
                          aria-expanded={showFormats}
                          aria-label="Which download should I choose, and how do I play it?">
                          ?
                        </button>
                      )}
                    >
                      <Popup.Header>{PLATFORM_LABELS[platform]}</Popup.Header>
                      <Popup.Content>
                        <List ordered size="small">
                          {STEPS[platform].map((step) => (
                            <List.Item key={step}>{step}</List.Item>
                          ))}
                        </List>
                        {/* ⓘ A plain anchor, not a react-router Link: the guide is a top-level route
                            and this page is deliberately outside the app’s router chrome. */}
                        <a href={GUIDE_URL} style={{ fontSize: '0.9em' }}>
                          All platforms, with pictures ›
                        </a>
                      </Popup.Content>
                    </Popup>
                  </p>

                  {/* ⓘ A Modal lived here for an hour on 2026-10-25, holding every platform’s
                      instructions. It covered the shelf to do it, which is what the popover above
                      and the guide article now solve between them. */}

                  {releases.map((r) => (
                    <section key={r.uuid} className="lp-row">
                      <div className="lp-row-cover">
                        {r.cover || r.coverFallback ? (
                          <CoverThumb release={r} />
                        ) : (
                          // ⓘ A book with no cover keeps the cell, so the rows stay aligned.
                          <div className="lp-cover-blank" aria-hidden="true" />
                        )}
                      </div>

                      <div className="lp-row-main">
                        <div className="lp-row-head">
                          <div className="lp-row-id">
                            <h2 className="lp-row-title">{r.title}</h2>
                            <p className="lp-row-meta lp-small lp-muted">{shelfMeta(r)}</p>
                          </div>

                          {/*
                        ⓘ ONE BUTTON WHEN THERE IS ONE FILE, which is every release today — six
                        whole-book MP3s. A `chapters` release has many items, and for that the
                        button becomes a disclosure and the chapters list below it does the
                        downloading. Both shapes exist in the data, so both are handled.
                      */}
                          {/*
                            ⭐ TWO FORMATS, NOT TWO CHAPTERS. Counting items would read "MP3 + m4b" as
                            two chapters and hide both behind a disclosure. ⓘ The audiobook is offered
                            first — it has the chapters, the position memory and the speed control, and
                            it is the smaller file. The MP3 stays for players that will not open an
                            m4b, Windows Media Player among them.
                          */}
                          {audiobookOf(r) && (
                            <FileRow item={audiobookOf(r)} token={token} onExpired={expired}
                              compact label="Audiobook"
                              hint="Chapters, keeps your place, speed control. On iPhone: Share → Books." />
                          )}
                          {plainFilesOf(r).length === 1 ? (
                            <FileRow item={plainFilesOf(r)[0]} token={token} onExpired={expired}
                              compact label={audiobookOf(r) ? 'MP3' : 'Download'}
                              hint={audiobookOf(r)
                                ? 'One long file. Plays anywhere, but no chapters or bookmarks.'
                                : 'The whole book as one audio file.'} />
                          ) : (
                            <button type="button" className="lp-button lp-button-sm"
                              onClick={() => setOpenChapters(openChapters === r.uuid ? null : r.uuid)}
                              aria-expanded={openChapters === r.uuid}>
                              {plainFilesOf(r).length} chapters
                            </button>
                          )}
                        </div>

                        {plainFilesOf(r).length > 1 && openChapters === r.uuid && (
                          <ul className="lp-files">
                            {plainFilesOf(r).map((it) => (
                              <FileRow key={it.uuid} item={it} token={token} onExpired={expired} />
                            ))}
                          </ul>
                        )}

                        <NotesBlock release={r} notes={notes} token={token} onExpired={expired}
                          onChanged={() => loadNotes(token)} />
                        {/*
                    ⭐ INSIDE THE LAST CARD, not below it (operator, 2026-10-12). Two reasons, and
                    the second was a defect: it reads as part of the downloads it describes, AND it
                    was failing contrast out on the page wash — `--lp-muted` is 3.33:1 against the
                    wash's darker top stop and 5.36:1 on a white card. Moving it passes WCAG AA
                    without touching a colour, which is why nothing here was lightened or darkened.

                    ⓘ The LAST card only, so it appears once however many releases a key grants.
                  */}
                      </div>
                    </section>
                  ))}

                  {/*
                ⓘ The download-location line is at the TOP of this shelf now, not here and not per
                row — the same sentence for every book, and advice a reader wants before the first
                tap. ⚠️ It stays inside this surface because `--lp-muted` is 3.33:1 on the page wash,
                which is why it lived in a card from 2026-10-12 until the grid removed the cards.
              */}
                </div>
              )}

              {/*
                ⭐ THE REASON ANY OF THIS EXISTS. The operator: *"There is no obvious way to enter a
                second key, without clicking Forget this key (which is not obvious to getting a
                different book key)."* Books arrive one at a time, each with its own key, so adding
                one is an ordinary action — not a consequence of discarding what you already have.

                ⓘ A plain action until used, so it does not compete with the downloads. The key box
                reuses KeyForm, and because a token is in hand `onKey` redeems straight away rather
                than asking anybody to sign in again.
              */}
              {releases && (addingKey ? (
                <div className="lp-card">
                  <KeyForm onKey={onKey} notice={null} />
                  <p className="lp-help lp-small">
                    <button type="button" className="lp-link" onClick={() => setAddingKey(false)}>
                      Cancel
                    </button>
                  </p>
                </div>
              ) : (
                <p className="lp-help">
                  <button type="button" className="lp-link" onClick={() => { setNotice(null); setAddingKey(true); }}>
                    Add another access key
                  </button>
                </p>
              ))}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default ListenPage;
